import { PATIENT_FRIENDLY_ROUTES, PATIENT_FRIENDLY_UNITS, type PatientFriendlyRoute } from '@liza/fhir-utils';
import { RXNORM } from '@medplum/core';
import type { ValueSetComposeIncludeFilter } from '@medplum/fhirtypes';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';
import type { ValueSetCode } from '../components/records/ValueSetCombobox';
import { deriveDoseForm } from './medicationDoseFormDerivation';
import { useRxNormProductLookup } from './useRxNormProductLookup';
import { useValueSetExpand, type UseValueSetExpandResult } from './useValueSetExpand';

/** `$expand` input shape — discriminated XOR between picking a concept set via an inline
 * `include` clause or via a `url` reference to a published ValueSet. The inner trpc
 * input is wider (more optional fields, plus an `offset` we don't use); we mirror the
 * subset we actually construct so callers get compile-time validation of the arguments
 * the hook builds. `ValueSetComposeIncludeFilter` is imported from `@medplum/fhirtypes`
 * so the `op` literal union stays in sync with FHIR. If trpc's expand input ever changes
 * the *required* shape of `include` or `url`, this type will silently drift — accepted
 * cost for a type-checked boundary. */
type ValueSetExpandInput =
  | {
      include: { system: string; filter?: ValueSetComposeIncludeFilter[] };
      url?: undefined;
      filter?: string;
      count?: number;
    }
  | { url: string; include?: undefined; filter?: string; count?: number };

/** Pass-through for `trpc.valueSet.expand.queryOptions`. The hook builds the three pickers'
 * (product / form / route) arguments internally; this factory just forwards them to the
 * app's trpc binding. The *input* is typed strictly so caller typos catch at compile time;
 * the *return* is `any` because each app's trpc proxy returns a tagged options shape that
 * doesn't quite assign to the inner hook's `UseQueryOptions<...>` parameter — the inner
 * `useValueSetExpand` validates the shape via its own generics at runtime/type-check. */
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type ValueSetExpandQueryOptionsFactory = (input: ValueSetExpandInput) => any;

/** Pass-through for `trpc.codeSystem.lookup.queryOptions` — same pattern, plain input shape. */
type CodeSystemLookupQueryOptionsFactory = (input: {
  system: string;
  code: string;
  properties?: string[];
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
}) => any;

interface UseMedicationFormDataSourcesParams {
  /** Pass-through to `trpc.valueSet.expand.queryOptions` — used internally to build the
   * RxNorm product picker. */
  valueSetExpand: ValueSetExpandQueryOptionsFactory;
  /** Pass-through to `trpc.codeSystem.lookup.queryOptions` — used internally to build the
   * dose-form lookup for the selected product. */
  codeSystemLookup: CodeSystemLookupQueryOptionsFactory;
  /** Gate the network queries — usually `true` for inline forms, `isOpen` for dialogs. */
  enabled?: boolean;
}

export interface UseMedicationFormDataSourcesResult {
  product: UseValueSetExpandResult;
  /** Per-dose amount from the product's ScriptSure sig, or null when it has none. Seeds the Dose
   * field — the dose form carries no dose, so only a sig can supply this. */
  derivedDose: number | null;
  /** Patient-friendly unit for the product (e.g. "tablet") — from the sig when it names one, else
   * the dose form. Null when neither resolves. Seeds the Units field. */
  derivedUnit: string | null;
  /** Patient-friendly route for the product (display + SNOMED code) — from the sig when it names one
   * (which also preserves any stated laterality), else the dose form. Seeds the Route field. */
  derivedRoute: PatientFriendlyRoute | null;
  /** Option set for the Units field — every patient-friendly unit in the table. */
  unitOptions: string[];
  /** Option set for the Route field — every patient-friendly route in the table. */
  routeOptions: PatientFriendlyRoute[];
  /** True while the dose-form lookup is in flight. Thread to the Units/Route `isLoading`. */
  isDoseFormFetching: boolean;
  /** Currently-selected product. */
  selectedProduct: ValueSetCode | null;
  /** Notify the hook when the user picks (or clears) a product — drives the dose-form lookup.
   * `sigLine` is the formulation's ScriptSure sig template, when it has one. */
  setSelectedProduct: (code: ValueSetCode | null, sigLine?: string) => void;
}

/**
 * Bundle the RxNorm product picker, the selected-product state, the dose-form lookup, and the
 * patient-friendly unit/route it resolves to — everything the Add Medication form renders against.
 * Each call site wires up its own trpc pass-through factories (so the hook stays inside `@liza/ui`
 * without taking a dep on app-specific trpc shapes); the hook owns the rest.
 *
 * Units and Route are no longer ValueSet pickers. They — and Dose — are derived from two signals, in
 * order: the formulation's ScriptSure sig template ("Inject 1 pen subcutaneously once weekly"), then
 * the RxNorm dose form (`dose_form_of` → `RXNORM_FORM_LOOKUP`) for whatever the sig left unset. The
 * dropdown option sets remain the table's full distinct lists, and every derived value is one of
 * them. See `deriveDoseForm` for why the sig wins.
 *
 * The consumer-friendly SCDC/SBDC medication display is resolved server-side at write time — the
 * form just sends the RxNorm SCD/SBD coding, the server picks the text. So this hook deliberately
 * does NOT expose a `consumerDisplay`; it only owns the data sources the form renders against.
 */
export function useMedicationFormDataSources({
  valueSetExpand,
  codeSystemLookup,
  enabled = true,
}: UseMedicationFormDataSourcesParams): UseMedicationFormDataSourcesResult {
  const [selectedProduct, setSelectedProductState] = useState<ValueSetCode | null>(null);
  const [sigLine, setSigLine] = useState<string | undefined>(undefined);

  // The sig belongs to the chosen formulation, so it is set and cleared with the product rather than
  // tracked separately — a stale sig would seed the previous formulation's dose onto a new product.
  const setSelectedProduct = useCallback((code: ValueSetCode | null, nextSigLine?: string): void => {
    setSelectedProductState(code);
    setSigLine(nextSigLine);
  }, []);

  const product = useValueSetExpand({
    queryOptions: (filter) =>
      valueSetExpand({
        // Dispensable product tiers (SBD/SCD) restricted to human-use drugs. Without `RXN_HUMAN_DRUG`
        // the typeahead offers oddball concepts (e.g. veterinary "aspirin 1 MG/MG Oral Powder") that
        // ScriptSure can't resolve and silently drops on sync (#5304).
        include: {
          system: RXNORM,
          filter: [
            { property: 'tty', op: 'in', value: 'SBD,SCD' },
            { property: 'RXN_HUMAN_DRUG', op: '=', value: 'US' },
          ],
        },
        filter,
        count: 100,
      }),
    enabled,
  });

  const {
    doseForm,
    isFetching: isDoseFormFetching,
    isSuccess: isDoseFormResolved,
  } = useRxNormProductLookup({
    queryOptions: () =>
      codeSystemLookup({ system: RXNORM, code: selectedProduct?.code ?? '', properties: ['dose_form_of'] }),
    rxNormSystem: RXNORM,
    enabled: enabled && Boolean(selectedProduct?.code),
  });

  // Resolve Dose / Units / Route from the ScriptSure sig first, then the RxNorm dose form for
  // whatever the sig left unset (#5730). See `deriveDoseForm` for the precedence rationale.
  const {
    derivedDose,
    derivedUnit,
    derivedRoute,
    unmappedDoseFormCode,
    unmappedDoseFormDisplay,
    productMissingDoseForm,
  } = deriveDoseForm({ selectedProduct, doseForm, isDoseFormResolved, sigLine });

  // A product resolved to a dose form the lookup table doesn't cover — the user gets no auto-filled
  // unit/route. The fixture-coverage test guards table-vs-RxNav drift at build time, but a live
  // product can still surface a DF we missed; report it so we can add the mapping. Keyed on the
  // code (a given code always resolves to the same display) so it reports once per unmapped form.
  useEffect(() => {
    if (unmappedDoseFormCode) {
      Sentry.captureException(new Error('RxNorm dose form has no patient-friendly mapping'), {
        tags: { feature: 'medicationForm' },
        extra: { doseFormCode: unmappedDoseFormCode, doseFormDisplay: unmappedDoseFormDisplay },
      });
    }
  }, [unmappedDoseFormCode, unmappedDoseFormDisplay]);

  // The terminology returned no dose form for a selected product at all — the `dose_form_of`
  // relationship is missing from the concept (not merely unmapped), which the check above cannot
  // see. This is the failure that left Zepbound's Units/Route empty with nothing in Sentry. Reported
  // even when the sig covered for it: the terminology data still needs fixing, and a formulation
  // with no sig template gets nothing at all. `hadSig` distinguishes those two cases in triage.
  const missingCode = productMissingDoseForm?.code;
  const missingDisplay = productMissingDoseForm?.display;
  const hadSig = Boolean(sigLine);
  useEffect(() => {
    if (missingCode) {
      Sentry.captureException(new Error('RxNorm product has no dose_form_of in the terminology'), {
        tags: { feature: 'medicationForm' },
        extra: { productCode: missingCode, productDisplay: missingDisplay, hadSig },
      });
    }
  }, [missingCode, missingDisplay, hadSig]);

  return {
    product,
    derivedDose,
    derivedUnit,
    derivedRoute,
    unitOptions: PATIENT_FRIENDLY_UNITS,
    routeOptions: PATIENT_FRIENDLY_ROUTES,
    isDoseFormFetching,
    selectedProduct,
    setSelectedProduct,
  };
}
